/**
 * Signin Page
 *
 * Full-YDM hero treatment: black background, large yellow logo lockup,
 * "Make It Unstoppable" tagline overhead, white card with yellow CTA.
 *
 * @module pages/auth/Signin
 */

import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Mail, Lock, Loader2, AlertCircle, Play } from "lucide-react";
import { signInWithEmail } from "@/config/supabase";
import { INVITE_RETURN_KEY } from "@/pages/AcceptInvite";
import { DEMO_MODE, DEMO_FEATURES, DEMO_CREDENTIALS } from "@/config/demoConfig";
import { START_PAGE } from "@/config/startPage";
import { logError } from '../../services/logger';
import { YDMWordmark, BrandFooter } from "@/components/brand";
import { Input } from "@/components";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  email?: string;
  password?: string;
}

const Signin: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const validateField = useCallback((field: "email" | "password", value: string) => {
    const errors: FieldErrors = {};
    if (field === "email") {
      if (!value.trim()) errors.email = t("auth.errors.emailRequired");
      else if (!EMAIL_REGEX.test(value)) errors.email = t("auth.errors.emailInvalid");
    }
    if (field === "password") {
      if (!value) errors.password = t("auth.errors.passwordRequired");
    }
    return errors[field] || undefined;
  }, [t]);

  const handleBlur = useCallback((field: "email" | "password", value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  }, [validateField]);

  const validateAll = useCallback((): boolean => {
    const errors: FieldErrors = {};
    if (!email.trim()) errors.email = t("auth.errors.emailRequired");
    else if (!EMAIL_REGEX.test(email)) errors.email = t("auth.errors.emailInvalid");
    if (!password) errors.password = t("auth.errors.passwordRequired");
    setFieldErrors(errors);
    setTouched({ email: true, password: true });
    return Object.keys(errors).length === 0;
  }, [email, password, t]);

  /**
   * An invite link parked by AcceptInvite when the visitor was not signed in.
   * Read once and cleared, so a later ordinary sign-in does not jump into a
   * stale invitation.
   */
  const consumeInviteReturn = (): string | null => {
    try {
      const target = sessionStorage.getItem(INVITE_RETURN_KEY);
      if (!target) return null;
      sessionStorage.removeItem(INVITE_RETURN_KEY);
      // Only ever an in-app invite path — never an arbitrary redirect target.
      return target.startsWith("/invite/accept?") ? target : null;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!validateAll()) return;

    setIsLoading(true);
    try {
      const result = await signInWithEmail(email, password, t);
      if (result.success) {
        navigate(consumeInviteReturn() ?? START_PAGE, { replace: true });
      } else {
        setFormError(result.error || t("auth.signinFailed"));
      }
    } catch (err) {
      setFormError(t("auth.unexpectedError"));
      logError('Auth', 'Sign in error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTryDemo = () => {
    setEmail(DEMO_CREDENTIALS.email);
    setPassword(DEMO_CREDENTIALS.password);
    setFieldErrors({});
    setTouched({ email: false, password: false });
  };

  const hasErrors = Object.values(fieldErrors).some(Boolean);

  return (
    <div className="min-h-screen bg-primary-500 flex flex-col items-center justify-center p-4 text-ink-950 relative overflow-hidden">
      {/* IDEAS gradient accents — vibrant bright per brand guideline p.22. Kept
          lighter blur radius so the browser can paint quickly. */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-ideas-orange/40 rounded-full blur-2xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-ideas-pink/30 rounded-full blur-2xl" />
      </div>

      <div className="relative max-w-md w-full">
        {/* Hero brand lockup — black logo on yellow */}
        <div className="text-center mb-8">
          <YDMWordmark variant="hero" bg="yellow" withTagline size="xl" />
          <h1 className="mt-6 font-display italic font-bold text-3xl sm:text-4xl text-ink-950 leading-tight">
            <Trans i18nKey="auth.makeItUnstoppable" />
          </h1>
          <p className="mt-3 text-xs uppercase tracking-[0.3em] text-ink-900 font-display font-semibold">
            {t("auth.mediaIntelligence")}
          </p>
        </div>

        {/* Form — white card with bold black border to anchor it on the yellow */}
        <div className="bg-white text-ink-900 rounded-2xl shadow-ink border-2 border-ink-950 p-8">
          {/* Form-level error (from server/auth) */}
          {formError && (
            <div role="alert" className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
              <span className="text-sm text-error-800">{formError}</span>
            </div>
          )}

          {/* Field-level error summary — shows all validation errors at once */}
          {hasErrors && (
            <div role="alert" aria-live="assertive" className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-semibold text-amber-900 mb-1">{t("auth.errors.fixFollowing")}</p>
              <ul className="text-sm text-amber-800 list-disc list-inside">
                {fieldErrors.email && <li>{fieldErrors.email}</li>}
                {fieldErrors.password && <li>{fieldErrors.password}</li>}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* Email */}
            <Input
              id="email"
              label={t("auth.emailAddress")}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (touched.email) setFieldErrors((prev) => ({ ...prev, email: validateField("email", e.target.value) }));
              }}
              onBlur={() => handleBlur("email", email)}
              placeholder="you@example.com"
              disabled={isLoading}
              leftIcon={<Mail className="w-5 h-5 text-ink-400" />}
              error={touched.email ? fieldErrors.email : undefined}
              aria-invalid={touched.email && !!fieldErrors.email}
              aria-describedby={fieldErrors.email ? "email-error" : undefined}
              className="py-3 border-ink-200 focus:border-primary-500 disabled:bg-ink-100"
            />
            {touched.email && fieldErrors.email && (
              <span id="email-error" className="sr-only">{fieldErrors.email}</span>
            )}

            {/* Password */}
            <Input
              id="password"
              label={t("auth.password")}
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (touched.password) setFieldErrors((prev) => ({ ...prev, password: validateField("password", e.target.value) }));
              }}
              onBlur={() => handleBlur("password", password)}
              placeholder={t("auth.passwordPlaceholder")}
              disabled={isLoading}
              leftIcon={<Lock className="w-5 h-5 text-ink-400" />}
              error={touched.password ? fieldErrors.password : undefined}
              aria-invalid={touched.password && !!fieldErrors.password}
              aria-describedby={fieldErrors.password ? "password-error" : undefined}
              className="py-3 border-ink-200 focus:border-primary-500 disabled:bg-ink-100"
            />
            {touched.password && fieldErrors.password && (
              <span id="password-error" className="sr-only">{fieldErrors.password}</span>
            )}

            {/* Submit — BLACK button (the primary CTA on a yellow canvas inverts to black). */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-ink-950 text-primary-500 font-bold uppercase tracking-wide rounded-lg hover:bg-ink-800 focus:outline-none focus:ring-2 focus:ring-ink-950 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-ink"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t("auth.signingIn")}
                </>
              ) : (
                t("auth.signin")
              )}
            </button>

            {/* Try Demo — outline black to keep two CTAs distinct on the yellow canvas */}
            {DEMO_MODE && DEMO_FEATURES.autoFillCredentials && (
              <button
                type="button"
                onClick={handleTryDemo}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-white text-ink-950 border-2 border-ink-950 font-bold uppercase tracking-wide rounded-lg hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-ink-950 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                {t("auth.tryDemo")}
              </button>
            )}
          </form>

          {/* Forgot password — the entry point to account recovery. Before this
              existed a forgotten password meant the account was gone: there was
              no reset flow of any kind in the product. */}
          <p className="mt-6 text-center text-sm">
            <Link
              to="/auth/forgot-password"
              className="font-bold text-ink-950 hover:text-ink-700 underline underline-offset-2 decoration-2"
            >
              {t("auth.forgotPassword")}
            </Link>
          </p>

          {/* Sign up link */}
          <p className="mt-3 text-center text-sm text-ink-700">
            {t("auth.dontHaveAccount")}{" "}
            <Link
              to="/auth/signup"
              className="font-bold text-ink-950 hover:text-ink-700 underline underline-offset-2 decoration-2"
            >
              {t("auth.createOneNow")}
            </Link>
          </p>
        </div>

        {/* Brand attribution — on yellow canvas */}
        <BrandFooter bg="yellow" className="mt-8" />
      </div>
    </div>
  );
};

export default Signin;
